import React, { useEffect, useState } from "react";
import {
  createOrgRequest,
  deleteOrgRequest,
  getAllDistributors,
  getOrganizationById,
  getOrgsRequests,
} from "@utils/api-communication/RestApiCli";
import {
  Button,
  Card,
  Container,
  Drawer,
  Flex,
  Title,
  Text,
  Divider,
  Group,
  Switch,
  Menu,
  Burger,
  Table,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import OrganizationManagementDashboard from "../organization_dashboard/OrganizationManagementDashboard";
import OrganizationRequestList from "../distributor_dashboard/OrganizationRequestList";
import {
  OrganizationAccountRequestForm,
  showCreateOrgRequestError,
} from "../distributor_dashboard/OrganizationAccountRequestForm";
import OfflineLicenseRequestList from "../distributor_dashboard/OfflineLicenseRequestList";
import { navigate } from "@utils/navigationService";
import { useTranslation } from "react-i18next";
import SubmissionsList from "@components/submissions/SubmissionsList";
import PaginatedOrganizations from "../common/PaginatedOrganizations";
import { OrganizationListRow } from "../common/organizationListQuery";
import { useNavigate } from "react-router-dom";

export default function AdminDashboard(_props) {
  const [activeDistributors, setActiveDistributors] = useState<any[]>([]);
  const [deactivatedDistributors, setDeactivatedDistributors] = useState<any[]>([]);
  const [distId, setDistId] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [orgRequests, setOrgRequests] = useState([]);
  const [opened, { open, close }] = useDisclosure(false);
  const [selectedOrganization, setSelectedOrganization] = useState<OrganizationListRow | null>(
    null,
  );
  const [menuOpened, setMenuOpened] = useState(false);

  const { t } = useTranslation();
  const navigateTo = useNavigate();

  useEffect(() => {
    updateLists();
  }, []);

  useEffect(() => {
    if (distId) {
      navigate("/distributor-dashboard?id=" + distId);
    }
  }, [distId]);

  const updateLists = () => {
    getAllDistributors(
      { includeDeleted: true },
      (res) => {
        const allDistributors = res?.data ?? [];
        setActiveDistributors(allDistributors.filter((dist: any) => !dist.deleted));
        setDeactivatedDistributors(allDistributors.filter((dist: any) => dist.deleted));
      },
      (error) => {
        console.error(error);
      },
    );

    getOrgsRequests(
      distId ? { id: distId } : {},
      (res) => {
        setOrgRequests(res.data);
      },
      (error) => {
        console.error(error);
      },
    );
  };

  const refreshSelectedOrganization = () => {
    const orgId = selectedOrganization?.orgId;
    if (!orgId) return;
    getOrganizationById(
      orgId,
      (res) => {
        setSelectedOrganization((current) =>
          current?.orgId === orgId ? { ...current, ...res.data } : current,
        );
      },
      (error) => {
        console.error(error);
      },
    );
  };

  const handleDeleteRequest = (id) => {
    deleteOrgRequest(
      { id },
      (res) => {
        updateLists();
        console.log(res);
      },
      (error) => {
        console.error(error);
      },
    );
  };

  return (
    <Container fluid size={"responsive"}>
      {selectedOrganization ? (
        <OrganizationManagementDashboard
          organization={selectedOrganization}
          back={() => {
            setSelectedOrganization(null);
            updateLists();
          }}
          updateOrganization={refreshSelectedOrganization}
        />
      ) : (
        <Container>
          <Drawer opened={opened} onClose={close} withCloseButton={true} position="right" size="lg">
            <OrganizationAccountRequestForm
              onSubmit={(body) => {
                createOrgRequest(
                  body,
                  distId,
                  (res) => {
                    console.log(res);
                    close();
                  },
                  (error) => {
                    console.error(error);
                    showCreateOrgRequestError(error, t);
                  },
                );
              }}
            />
          </Drawer>
          <Flex justify={"space-between"} align={"center"}>
            <Group>
              <Menu
                opened={menuOpened}
                onChange={setMenuOpened}
                shadow="md"
                width={200}
                position="bottom-start"
              >
                <Menu.Target>
                  <Burger
                    opened={menuOpened}
                    onClick={() => setMenuOpened((o) => !o)}
                    size="sm"
                    mr="md"
                  />
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item onClick={() => navigateTo("/scope-management")}>
                    Scope Management
                  </Menu.Item>
                  <Menu.Item onClick={() => navigateTo("/version-management")}>
                    Version Management
                  </Menu.Item>
                  <Menu.Item onClick={() => navigateTo("/dist")}>Distributor Management</Menu.Item>
                  <Menu.Item onClick={() => navigateTo("/product-management")}>
                    Product Management
                  </Menu.Item>
                  <Menu.Item onClick={() => navigateTo("/screening-payments")}>
                    Screening Payments
                  </Menu.Item>
                  <Menu.Item onClick={() => navigateTo("/dconfigs")}>Default Configs</Menu.Item>
                  <Menu.Item onClick={() => navigateTo("/system-logs")}>System Logs</Menu.Item>
                  <Menu.Item onClick={() => navigateTo("/notification-setup")}>
                    Notification Setup
                  </Menu.Item>
                  <Menu.Item onClick={() => navigateTo("/admin-configurations")}>
                    {t("adminConfigurations.title")}
                  </Menu.Item>
                  <Menu.Item onClick={() => navigate("/orders")}>Orders</Menu.Item>
                  <Menu.Item onClick={() => navigate("/licenses")}>Licenses</Menu.Item>
                  <Menu.Item onClick={() => navigateTo("/invoices")}>Invoices</Menu.Item>
                  <Menu.Item onClick={() => navigateTo("/invoice-sku-types")}>
                    Invoice SKU Types
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
              <Title my={"sm"}>Admin Dashboard</Title>
            </Group>
            <Group>
              <Button onClick={open}>Request</Button>
              <Switch
                onChange={() => setShowDetails(!showDetails)}
                onLabel={t("Show")}
                offLabel={t("Hide")}
              />
            </Group>
          </Flex>
          {orgRequests.length > 0 && (
            <>
              <Card mt={"sm"} p={"sm"} style={{ background: "var(--oculera-surface-alt)" }}>
                <OrganizationRequestList
                  orgRequests={orgRequests}
                  deleteRequest={handleDeleteRequest}
                />
              </Card>
              <Divider size={"sm"} mt={"sm"} mb={"sm"} />
            </>
          )}
          <SubmissionsList />
          <OfflineLicenseRequestList />
          <Text mt={"sm"}> Organizations </Text>
          <PaginatedOrganizations
            showDetails={showDetails}
            onOrganizationSelect={setSelectedOrganization}
            onDistSelect={setDistId}
            showCountry={true}
            showDistId={true}
            dateLocale="tr"
            distributorOptions={activeDistributors.map((dist: any) => ({
              value: dist.distId,
              label: dist.name ? `${dist.name} (${dist.distId})` : dist.distId,
            }))}
          />
          {deactivatedDistributors.length > 0 && (
            <>
              <Text mt={"lg"} c="red">
                Deactivated Distributors
              </Text>
              <Table highlightOnHover striped verticalSpacing="xs" mt="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>No</Table.Th>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Country</Table.Th>
                    <Table.Th>Email</Table.Th>
                    <Table.Th>Dist Id</Table.Th>
                    {showDetails ? <Table.Th>Org IDs</Table.Th> : null}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {deactivatedDistributors.map((dist, index) => (
                    <Table.Tr key={dist.distId} style={{ color: "var(--mantine-color-red-7)" }}>
                      <Table.Td>{index + 1}</Table.Td>
                      <Table.Td>{dist.name || "-"}</Table.Td>
                      <Table.Td>
                        {Array.isArray(dist.country) ? dist.country.join(", ") : "-"}
                      </Table.Td>
                      <Table.Td>
                        {Array.isArray(dist.emails) ? dist.emails.join(", ") : "-"}
                      </Table.Td>
                      <Table.Td
                        style={{ cursor: "pointer" }}
                        onClick={() => setDistId(dist.distId)}
                      >
                        {dist.distId}
                      </Table.Td>
                      {showDetails ? <Table.Td>{dist.orgIds?.join(", ") || "-"}</Table.Td> : null}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </>
          )}
        </Container>
      )}
    </Container>
  );
}
